/* The project workspace: upload, run, and the six result views.

   Everything the prototype computed in the browser now comes from the run endpoints, but
   the interactions are unchanged — filters, sorting and paging drive one request, and
   every chart segment, topic card, journey node and action card filters the query table
   and jumps to it. */

import { $, esc, fmt } from "../util.js";
import { paintIcons } from "../icons.js";
import { api, apiUrl } from "../api.js";
import { toast } from "../toast.js";
import { state } from "../state.js";
import { renderStepper } from "../components/stepper.js";
import { renderTable, scrollTableTop } from "../views/table.js";
import { openDrawer } from "../views/drawer.js";
import { renderFilterOptions, syncFilterControls, renderPills, renderChips, markActiveFilters, EMPTY_FILTERS } from "../views/filters.js";
import { renderCharts, initChartToggle } from "../views/charts.js";
import { renderTopics, initTopicToggle } from "../views/topics.js";
import { renderJourney } from "../views/journey.js";
import { renderActions } from "../views/actions.js";
import { renderRef } from "../views/reference.js";

const TABS = ["queries", "overview", "topics", "journey", "actions", "ref"];

/* Everything about the open run. */
const view = {
  runId: null,
  summary: null,
  facets: null,
  f: { bucket: "all", stage: "all", journey: "all", action: "all", conf: "0", ai: "all", search: "", topic: null },
  sortKey: "imp",
  sortDir: "desc",
  page: 0,
  size: 50,
  loaded: {},           // which tabs have been rendered for this run
  upload: { queries: null, pages: null },
};

let wired = false;
let onSideRefresh = () => {};

export async function openProjectWorkspace(project, { onRefreshSidebar }) {
  onSideRefresh = onRefreshSidebar || (() => {});
  wireOnce();
  resetForProject(project);

  $("proj-name").innerHTML = '<i data-ic="folder" style="color:var(--violet)"></i>' + esc(project.name);
  paintIcons($("proj-name").parentElement);
  $("proj-sub").textContent = "";
  $("proj-stats").innerHTML = "";
  renderStepper(3);
  renderRef();

  try {
    const { run } = await api.latestRun(project.id);
    if (run && run.status === "complete") {
      await loadRun(run);
      return;
    }
  } catch (err) {
    toast(err.message);
  }
  /* No results yet: show the upload strip and the stepper. The sample project never
     needs an upload, so it keeps the strip hidden. */
  $("upstrip").classList.remove("hidden");
  $("results").classList.add("hidden");
  $("fs-proj").classList.remove("hidden");
}

function resetForProject(project) {
  state.currentProject = project;
  state.run = null;
  state.tab = "queries";
  view.runId = null;
  view.summary = null;
  view.facets = null;
  view.loaded = {};
  view.page = 0;
  view.sortKey = "imp";
  view.sortDir = "desc";
  view.f = { bucket: "all", stage: "all", journey: "all", action: "all", conf: "0", ai: "all", search: "", topic: null };
  view.upload = { queries: null, pages: null };

  for (const id of ["drop-q", "drop-p"]) $(id).classList.remove("loaded", "over");
  $("h-q").textContent = "Drop your GSC Search Queries export or click — query · clicks · impressions · ctr · position (+ page column enables URL mapping)";
  $("h-p").textContent = "page · impressions";
  $("btn-analyse").disabled = true;
  setTab("queries");
}

/* ---------- upload & run ---------- */

function wireDropZone(dropId, inputId, kind) {
  const drop = $(dropId), input = $(inputId);
  drop.addEventListener("click", () => input.click());
  input.addEventListener("change", () => input.files[0] && handleFile(input.files[0], kind, dropId));
  ["dragover", "dragenter"].forEach(ev => drop.addEventListener(ev, e => { e.preventDefault(); drop.classList.add("over"); }));
  ["dragleave", "drop"].forEach(ev => drop.addEventListener(ev, e => { e.preventDefault(); drop.classList.remove("over"); }));
  drop.addEventListener("drop", e => e.dataTransfer.files[0] && handleFile(e.dataTransfer.files[0], kind, dropId));
}

async function handleFile(file, kind, dropId) {
  const label = kind === "queries" ? "Queries" : "Pages";
  try {
    const res = await api.upload(state.currentProject.id, kind, file);
    view.upload[kind] = res.uploadId;
    $(dropId).classList.add("loaded");
    $(kind === "queries" ? "h-q" : "h-p").textContent =
      "✓ " + file.name + " — " + fmt(res.rowCount) + (kind === "queries" ? " queries" : " pages");
    if (kind === "queries") $("btn-analyse").disabled = false;
  } catch (err) {
    toast(label + ": " + err.message);
  }
}

async function runAnalysis() {
  if (!view.upload.queries) return;
  /* The button steps aside entirely while the bar does the talking. */
  $("btn-analyse").disabled = true;
  $("btn-analyse").classList.add("hidden");
  showProgress(true);
  try {
    const { run } = await api.startRun(state.currentProject.id, view.upload.queries, view.upload.pages);
    const finished = await pollRun(run);
    if (finished.status !== "complete") throw new Error(finished.error || "That run did not finish");
    toast("Analysed " + fmt(finished.summary.totalQ) + " queries");
    await loadRun(finished);
    showProgress(false);
  } catch (err) {
    toast(err.message);
    showProgress(false);
    $("btn-analyse").classList.remove("hidden");
    $("btn-analyse").disabled = false;
    $("btn-analyse").innerHTML = '<i data-ic="play" style="width:13px;height:13px"></i>Run analysis';
    paintIcons($("btn-analyse"));
  }
}

/* Runs execute in the background, and a model-backed run of a large export takes real time,
   so this shows a proper bar with the count and a rough time remaining rather than leaving
   people staring at a frozen button. */
function showProgress(on) {
  const panel = $("run-progress");
  panel.classList.toggle("hidden", !on);
  if (on) {
    panel.classList.add("indeterminate");
    panel.classList.remove("done");
    $("rp-label").textContent = "Classifying";
    $("rp-pct").textContent = "0%";
    $("rp-fill").style.width = "";
    $("rp-meta").textContent = "Preparing your queries…";
  }
}

function updateProgress(run, startedAt) {
  const panel = $("run-progress");
  if (!(run.total > 0)) return;

  const pct = Math.min(100, Math.round(run.processed / run.total * 100));
  panel.classList.remove("indeterminate");
  $("rp-pct").textContent = pct + "%";
  $("rp-fill").style.width = pct + "%";

  let meta = `${fmt(run.processed)} of ${fmt(run.total)} queries`;
  const elapsed = (Date.now() - startedAt) / 1000;
  /* Only estimate once there is enough done for the figure to mean anything. */
  if (run.processed > 30 && elapsed > 5 && pct < 100) {
    const remaining = Math.round(elapsed / run.processed * (run.total - run.processed));
    if (remaining > 0) meta += ` · about ${humanDuration(remaining)} left`;
  }
  $("rp-meta").textContent = meta;
}

function humanDuration(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${mins % 60}m`;
}

async function pollRun(run) {
  const startedAt = Date.now();
  let current = run;
  while (current.status !== "complete" && current.status !== "error") {
    await new Promise(r => setTimeout(r, 1000));
    current = (await api.run(run.id)).run;
    if (current.status === "running") updateProgress(current, startedAt);
  }
  if (current.status === "complete") {
    const panel = $("run-progress");
    panel.classList.remove("indeterminate");
    panel.classList.add("done");
    $("rp-label").textContent = "Done";
    $("rp-pct").textContent = "100%";
    $("rp-fill").style.width = "100%";
    $("rp-meta").textContent = `${fmt(current.rowCount || current.total || 0)} queries classified`;
  }
  return current;
}

/* ---------- results ---------- */

async function loadRun(run) {
  view.runId = run.id;
  view.summary = run.summary;
  state.run = run;

  view.facets = await api.facets(run.id);
  renderFilterOptions(view.facets);
  renderHeaderStats(run);

  $("upstrip").classList.add("hidden");
  $("results").classList.remove("hidden");
  /* Explains an absent Page column rather than leaving people wondering. */
  $("nopage-note").classList.toggle("hidden", !!view.summary.hasPages);
  $("fs-proj").classList.add("hidden");   // the stepper has done its job
  onSideRefresh();

  view.loaded = {};
  await refreshTable();
  await renderCurrentTab();
}

function renderHeaderStats(run) {
  const s = run.summary;
  $("proj-stats").innerHTML =
    '<div class="pstat"><b style="color:var(--violet)">' + fmt(s.totalQ) + "</b><span>Queries</span></div>" +
    '<div class="pstat"><b style="color:var(--magenta)">' + fmt(s.conv) + "</b><span>Conversational</span></div>" +
    '<div class="pstat"><b style="color:var(--teal)">' + s.share.toFixed(1) + "%</b><span>Share</span></div>" +
    (s.hasAI ? '<div class="pstat"><b style="color:#0EA5E9">' + fmt(s.aiPages) + "</b><span>On AI pages</span></div>" : "");
  $("proj-sub").textContent = "your GSC export · analysed " +
    new Date(run.completedAt || run.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

/* ---------- tabs ---------- */

export function setTab(t) {
  state.tab = t;
  for (const tab of TABS) $("tab-" + tab).classList.toggle("hidden", tab !== t);
  onSideRefresh();
  if (view.runId) renderCurrentTab();
}

async function renderCurrentTab() {
  if (!view.runId) return;
  const t = state.tab;
  const ctx = {
    onFilter: applyFilter,
    onExportAction: exportAction,
    isDemo: !!state.currentProject?.demo,
  };
  try {
    if (t === "overview" && !view.loaded.overview) {
      const [buckets, stages, bands] = await Promise.all([
        api.aggregates(view.runId, "bucket"),
        api.aggregates(view.runId, "stage"),
        api.aggregates(view.runId, "positionBand"),
      ]);
      view.data = { ...view.data, buckets: buckets.groups, stages: stages.groups, bands: bands.bands };
      view.loaded.overview = true;
    }
    if (t === "overview") renderCharts({ buckets: view.data.buckets, stages: view.data.stages, bands: view.data.bands }, ctx);

    if (t === "topics") {
      if (!view.loaded.topics) {
        view.data = { ...view.data, topics: (await api.aggregates(view.runId, "topic")).groups };
        view.loaded.topics = true;
      }
      renderTopics(view.data.topics, ctx);
    }

    if (t === "journey") {
      if (!view.loaded.journey) {
        const [j, ins] = await Promise.all([api.aggregates(view.runId, "journey"), api.insights(view.runId)]);
        view.data = { ...view.data, journey: { groups: j.groups, phases: j.phases, insights: ins.insights } };
        view.loaded.journey = true;
      }
      renderJourney(view.data.journey, ctx);
    }

    if (t === "actions") {
      if (!view.loaded.actions) {
        view.data = { ...view.data, actions: (await api.actions(view.runId)).actions };
        view.loaded.actions = true;
      }
      renderActions(view.data.actions, ctx);
    }
  } catch (err) {
    toast(err.message);
  }
  paintIcons();
}

/* ---------- table + filters ---------- */

async function refreshTable() {
  if (!view.runId) return;
  renderChips(view.f, key => { view.f[key] = null; view.page = 0; refreshTable(); });
  renderPills(view.facets, view.f, patch => applyFilter(patch, { stay: true }));
  syncFilterControls(view.f);
  markActiveFilters(view.f);
  try {
    const page = await api.queries(view.runId, {
      ...cleanFilters(view.f),
      sortKey: view.sortKey, sortDir: view.sortDir,
      page: view.page, size: view.size,
    });
    renderTable(page, {
      sortKey: view.sortKey, sortDir: view.sortDir,
      hasAI: view.summary.hasAI, hasPages: view.summary.hasPages,
      scale: view.summary.scale || { imp50: 0, imp90: 0, clk50: 0, clk90: 0 },
      onSort: key => {
        if (view.sortKey === key) view.sortDir = view.sortDir === "desc" ? "asc" : "desc";
        else { view.sortKey = key; view.sortDir = "desc"; }
        refreshTable();
      },
      onOpenRow: async idx => {
        try { openDrawer(await api.queryDetail(view.runId, idx)); } catch (err) { toast(err.message); }
      },
    });
  } catch (err) {
    toast(err.message);
  }
}

/* One place where cross-view clicks land: set the filter, sync the control, show the table. */
function applyFilter(patch, { stay = false } = {}) {
  Object.assign(view.f, patch);
  view.page = 0;
  if (!stay) setTab("queries");
  syncFilterControls(view.f);
  refreshTable();
}

function cleanFilters(f) {
  const out = {};
  for (const [k, v] of Object.entries(f)) {
    if (v == null || v === "" || v === "all" || (k === "conf" && v === "0")) continue;
    out[k] = v;
  }
  return out;
}

/* ---------- exports ---------- */

function exportUrl(path, params) {
  const qs = new URLSearchParams(params).toString();
  return apiUrl(path) + (qs ? "?" + qs : "");
}

function downloadFiltered() {
  window.location.href = exportUrl(`/runs/${view.runId}/export.csv`, {
    ...cleanFilters(view.f), sortKey: view.sortKey, sortDir: view.sortDir,
  });
  toast("Preparing your CSV…");
}

function exportAction(key) {
  window.location.href = exportUrl(`/runs/${view.runId}/actions/${key}/export.csv`, {});
  toast("Preparing the work order…");
}

/* ---------- one-time wiring ---------- */

function wireOnce() {
  if (wired) return;
  wired = true;

  wireDropZone("drop-q", "file-q", "queries");
  wireDropZone("drop-p", "file-p", "pages");
  $("btn-analyse").addEventListener("click", runAnalysis);

  $("q-search").addEventListener("input", debounce(e => {
    view.f.search = e.target.value.trim();
    view.page = 0;
    refreshTable();
  }, 250));

  const selectFilters = { "f-bucket": "bucket", "f-stage": "stage", "f-journey": "journey", "f-conf": "conf", "f-ai": "ai", "f-action": "action" };
  for (const [id, key] of Object.entries(selectFilters)) {
    $(id).addEventListener("change", e => { view.f[key] = e.target.value; view.page = 0; refreshTable(); });
  }

  ["", "-b"].forEach(sfx => {
    $("pg-prev" + sfx).addEventListener("click", () => { view.page--; refreshTable(); if (sfx) scrollTableTop(); });
    $("pg-next" + sfx).addEventListener("click", () => { view.page++; refreshTable(); if (sfx) scrollTableTop(); });
    $("pg-size" + sfx).addEventListener("change", e => { view.size = +e.target.value; view.page = 0; refreshTable(); });
  });

  $("btn-export").addEventListener("click", downloadFiltered);
  $("btn-freset").addEventListener("click", () => {
    Object.assign(view.f, EMPTY_FILTERS);
    view.page = 0;
    refreshTable();
  });
  initChartToggle(() => renderCurrentTab());
  initTopicToggle(() => renderCurrentTab());
}

function debounce(fn, ms) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
}

view.data = {};
